/*
  Dinowax header — mobile menu, and touch handling for the dropdowns.

  Desktop dropdowns are pure CSS (:hover / :focus-within), so they work with this
  file absent and keyboard users get them via focus. This adds only what CSS
  cannot do: the mobile panel toggle, and tap-to-open on touch devices, where
  :hover would otherwise open a dropdown on the same tap that follows the link.
*/
(function () {
  'use strict';

  var OPEN = 'data-dw-header-open';
  var ITEM_OPEN = 'dw-header__item--open';
  var BODY_OPEN = 'dw-menu-open';

  /*
   * The mobile panel is position:fixed, so it needs the header's bottom edge in
   * VIEWPORT coordinates — and that edge moves. The announcement bar scrolls
   * away, the search row wraps at some widths, and a sticky header only stops
   * travelling once it is stuck. Hard-coding a height leaves either a gap above
   * the menu or a first item hidden behind the logo, depending on scroll
   * position, so it is measured instead.
   */
  function syncPanelOffset(root) {
    var bar = root.querySelector('.dw-header__utility') || root;
    var bottom = bar.getBoundingClientRect().bottom;
    if (bottom < 0) bottom = 0;
    root.style.setProperty('--dw-header-bottom', Math.round(bottom) + 'px');
  }

  /* The panel is hidden by a CSS media query, but the open attribute and the
     scroll lock are not. Closing at a hardcoded width while the CSS switched at
     another would strand both: the menu would vanish and the page behind would
     stay unscrollable. Read the breakpoint the CSS actually uses. */
  function breakpoint(root) {
    var v = parseInt(root.getAttribute('data-dw-mobile-breakpoint'), 10);
    return isNaN(v) ? 1024 : v;
  }


  /* ---- predictive search -------------------------------------------------
     Uses Shopify's /search/suggest.json, which is available on every store
     regardless of theme version. The form still submits to /search normally, so
     with JavaScript off this is just a search box.

     Prices come back as decimal strings ("14.00"), not cents, so they are
     dropped into the shop's money_format rather than divided by anything. The
     format is passed in from Liquid with the HTML stripped, and everything is
     written with textContent — product titles are merchant data and must never
     be injected as markup.
  -------------------------------------------------------------------------- */
  function setupSearch(form) {
    if (!form || form.__dwSearchBound) return;
    form.__dwSearchBound = true;

    if (form.getAttribute('data-suggest') !== 'true') return;

    var input = form.querySelector('[data-dw-search-input]');
    var panel = form.querySelector('[data-dw-search-results]');
    var list = form.querySelector('[data-dw-search-list]');
    var allLink = form.querySelector('[data-dw-search-all]');
    if (!input || !panel || !list || !allLink) return;

    var limit = parseInt(form.getAttribute('data-limit'), 10) || 6;
    var format = form.getAttribute('data-money-format') || '{{amount}}';
    var allLabel = form.getAttribute('data-all-label') || 'See all results for';
    var emptyLabel = form.getAttribute('data-empty-label') || 'No products match';
    var searchUrl = form.getAttribute('action') || '/search';

    var timer = null, seq = 0, active = -1, controller = null;

    function money(value) {
      return format.replace(/\{\{\s*[a-z_]+\s*\}\}/i, value);
    }

    function close() {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      active = -1;
    }

    function open() {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function rows() {
      return Array.prototype.slice.call(list.querySelectorAll('.dw-header__result'));
    }

    function highlight(next) {
      var items = rows();
      if (!items.length) return;
      if (active > -1 && items[active]) items[active].setAttribute('aria-selected', 'false');
      active = (next + items.length) % items.length;
      items[active].setAttribute('aria-selected', 'true');
      items[active].scrollIntoView({ block: 'nearest' });
    }

    function render(products, term) {
      list.textContent = '';
      active = -1;

      if (!products.length) {
        var empty = document.createElement('li');
        empty.className = 'dw-header__results-empty';
        empty.textContent = emptyLabel + ' “' + term + '”';
        list.appendChild(empty);
        allLink.textContent = '';
        allLink.removeAttribute('href');
        open();
        return;
      }

      products.forEach(function (p) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'dw-header__result';
        a.href = p.url;
        a.setAttribute('role', 'option');
        a.setAttribute('aria-selected', 'false');

        if (p.featured_image && p.featured_image.url) {
          var img = document.createElement('img');
          img.src = p.featured_image.url;
          img.alt = '';
          img.loading = 'lazy';
          a.appendChild(img);
        }

        var text = document.createElement('span');
        text.className = 'dw-header__result-text';

        var title = document.createElement('span');
        title.className = 'dw-header__result-title';
        title.textContent = p.title;
        text.appendChild(title);

        if (p.price != null) {
          var price = document.createElement('span');
          price.className = 'dw-header__result-price';
          price.textContent = money(p.price);
          text.appendChild(price);
        }

        a.appendChild(text);
        li.appendChild(a);
        list.appendChild(li);
      });

      allLink.textContent = allLabel + ' “' + term + '”';
      allLink.href = searchUrl + '?q=' + encodeURIComponent(term) + '&type=product';
      open();
    }

    function query(term) {
      var mine = ++seq;
      // Abandon a request the shopper has already typed past.
      if (controller) controller.abort();
      controller = ('AbortController' in window) ? new AbortController() : null;

      var url = searchUrl + '/suggest.json'
        + '?q=' + encodeURIComponent(term)
        + '&resources[type]=product'
        + '&resources[limit]=' + limit
        + '&resources[options][unavailable_products]=last';

      fetch(url, {
        credentials: 'same-origin',
        signal: controller ? controller.signal : undefined
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (mine !== seq) return;
          var products = (((data.resources || {}).results || {}).products) || [];
          render(products, term);
        })
        .catch(function () { /* aborted or offline — leave the panel as it is */ });
    }

    input.addEventListener('input', function () {
      var term = input.value.trim();
      clearTimeout(timer);
      if (term.length < 2) { close(); return; }
      timer = setTimeout(function () { query(term); }, 200);
    });

    input.addEventListener('keydown', function (e) {
      if (panel.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); highlight(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(active - 1); }
      else if (e.key === 'Enter' && active > -1) {
        var items = rows();
        if (items[active]) { e.preventDefault(); window.location.href = items[active].href; }
      } else if (e.key === 'Escape') { close(); }
    });

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= 2 && list.children.length) open();
    });

    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) close();
    });
  }


  /* ---- mega menu ---------------------------------------------------------
     Panels are empty in the HTML and fetched the first time one is opened:

         /collections/{handle}?section_id=dw-menu-panel

     A section cannot take parameters, but it inherits the Liquid context of the
     path it is requested against — so the collection URL IS the parameter. That
     matters because referencing collection.products inline fetches 50 records
     per collection on every page of the site, regardless of any `limit:`.

     Each panel is fetched once and kept. The URL set is five and identical for
     every visitor, so Shopify's origin cache serves them warm after the first
     hit anywhere.
  ------------------------------------------------------------------------- */
  var OPEN_DELAY = 130;   // long enough that sweeping across the nav does not strobe panels
  var CLOSE_DELAY = 220;  // long enough to cross the gap between the item and the panel

  function setupMega(root) {
    var items = root.querySelectorAll('[data-dw-mega-item]');
    if (!items.length) return;

    var scrim = root.parentNode ? root.parentNode.querySelector('[data-dw-scrim]') : null;
    var openTimer = null, closeTimer = null, current = null;

    function scrimTop() {
      // Never cover anything above the header, whatever the promo bar is doing.
      var r = root.getBoundingClientRect();
      document.documentElement.style.setProperty('--dw-scrim-top', Math.max(0, r.bottom) + 'px');
    }

    function load(panel) {
      if (panel.__dwLoaded || panel.__dwLoading) return;
      var url = panel.getAttribute('data-dw-mega-url');
      var body = panel.querySelector('[data-dw-mega-body]');
      if (!url || !body) return;
      panel.__dwLoading = true;

      fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('mega: HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          // section_id returns the whole section wrapper. Unwrap it rather than
          // cherry-picking the panel div: the section's <style> is a SIBLING of
          // that div, so selecting the div alone drops the CSS and the cards
          // render unstyled. Unwrapping also keeps the duplicate
          // shopify-section-dw-menu-panel id out of the DOM, which would
          // otherwise repeat once per panel.
          var box = document.createElement('div');
          box.innerHTML = html;
          var wrapper = box.querySelector('[id^="shopify-section-"]') || box;

          body.textContent = '';
          while (wrapper.firstChild) body.appendChild(wrapper.firstChild);

          // The panel's rules are scoped by class, not by section id, so one
          // copy styles every panel. Keep the first and drop the rest, or each
          // collection opened would add an identical stylesheet.
          var styles = body.querySelectorAll('style');
          for (var i = 0; i < styles.length; i++) {
            if (document.querySelector('style[data-dw-menu-panel-css]')) {
              styles[i].parentNode.removeChild(styles[i]);
            } else {
              styles[i].setAttribute('data-dw-menu-panel-css', '');
            }
          }
          panel.__dwLoaded = true;
        })
        .catch(function () {
          // Leave the panel usable — the head still links to the collection.
          var p = body.querySelector('.dw-mega__loading');
          if (p) p.className = 'dw-mega__error';
        })
        .then(function () { panel.__dwLoading = false; });
    }

    /* Hides the open panel but leaves the scrim alone, so moving between two
       collection items swaps panels without the background flashing back. */
    function hideCurrent() {
      if (!current) return;
      current.classList.remove('is-open');
      // Deliberately NOT setting .hidden: display:none would cut the collapse
      // animation dead. The closed state's visibility:hidden already takes the
      // panel out of the tab order.
      current = null;
    }

    function open(item) {
      var panel = item.querySelector('[data-dw-mega]');
      if (!panel || current === panel) return;
      hideCurrent();
      load(panel);
      panel.classList.add('is-open');
      current = panel;
      scrimTop();
      if (scrim) scrim.classList.add('is-open');
    }

    function closeAll() {
      hideCurrent();
      if (scrim) scrim.classList.remove('is-open');
    }

    Array.prototype.forEach.call(items, function (item) {
      item.addEventListener('mouseenter', function () {
        if (window.matchMedia('(hover: none)').matches) return;
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        openTimer = setTimeout(function () { open(item); }, OPEN_DELAY);
      });

      item.addEventListener('mouseleave', function () {
        if (window.matchMedia('(hover: none)').matches) return;
        clearTimeout(openTimer);
        clearTimeout(closeTimer);
        closeTimer = setTimeout(closeAll, CLOSE_DELAY);
      });

      // Keyboard: opening on focus is what makes this usable without a mouse.
      item.addEventListener('focusin', function () {
        // ...but not on touch. The panel is display:none below the mobile
        // breakpoint, and Android Chrome focuses an anchor on touch-down, so
        // this was buying a ~30KB fetch for something nobody can see.
        if (window.matchMedia('(hover: none)').matches) return;
        open(item);
      });
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) closeAll();
      });
    });

    if (scrim) {
      scrim.addEventListener('click', closeAll);
      scrim.addEventListener('mouseenter', function () {
        clearTimeout(openTimer);
        closeAll();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });

    window.addEventListener('resize', function () { if (current) scrimTop(); });
    window.addEventListener('scroll', function () { if (current) scrimTop(); }, { passive: true });
  }

  function setup(root) {
    if (!root || root.__dwHeaderBound) return;
    root.__dwHeaderBound = true;

    var toggle = root.querySelector('[data-dw-header-toggle]');
    var nav = root.querySelector('.dw-header__nav');

    function close() {
      root.removeAttribute(OPEN);
      document.documentElement.classList.remove(BODY_OPEN);
      document.body.classList.remove(BODY_OPEN);
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    function open() {
      syncPanelOffset(root);
      root.setAttribute(OPEN, '');
      document.documentElement.classList.add(BODY_OPEN);
      document.body.classList.add(BODY_OPEN);
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }

    if (toggle && nav) {
      toggle.addEventListener('click', function () {
        if (root.hasAttribute(OPEN)) close(); else open();
      });

      /* The offset is only correct for where the header is right now, so it is
         re-measured while the panel is open rather than once on opening. */
      window.addEventListener('scroll', function () {
        if (root.hasAttribute(OPEN)) syncPanelOffset(root);
      }, { passive: true });
      window.addEventListener('resize', function () {
        if (root.hasAttribute(OPEN)) syncPanelOffset(root);
      });
    }

    /* Searching and browsing the menu are two full-width surfaces competing for
       the same screen. Reaching for the search field is an unambiguous signal
       about which one is wanted, so the panel gets out of the way rather than
       leaving the shopper to find the close button first. */
    var searchInput = root.querySelector('[data-dw-search-input]');
    if (searchInput) {
      searchInput.addEventListener('focus', function () {
        if (root.hasAttribute(OPEN)) close();
      });
    }

    // Parent items with children: on the mobile panel, and on touch devices
    // generally, the first tap opens the submenu instead of navigating.
    root.querySelectorAll('.dw-header__item--has-menu > .dw-header__link').forEach(function (link) {
      link.addEventListener('click', function (event) {
        var item = link.parentElement;
        var isMobilePanel = root.hasAttribute(OPEN);
        var isTouch = window.matchMedia('(hover: none)').matches;
        if (!isMobilePanel && !isTouch) return;      // desktop pointer: follow the link
        if (item.classList.contains(ITEM_OPEN)) return; // already open: second tap follows it
        event.preventDefault();
        // Only one open at a time, so a long menu does not become a wall.
        root.querySelectorAll('.' + ITEM_OPEN).forEach(function (other) {
          if (other !== item) other.classList.remove(ITEM_OPEN);
        });
        item.classList.add(ITEM_OPEN);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.hasAttribute(OPEN)) {
        close();
        if (toggle) toggle.focus();
      }
    });

    // A tap outside the header closes anything left open on touch.
    document.addEventListener('click', function (e) {
      if (root.contains(e.target)) return;
      root.querySelectorAll('.' + ITEM_OPEN).forEach(function (i) { i.classList.remove(ITEM_OPEN); });
    });

    // Leaving the mobile breakpoint should not strand the panel open — nor,
    // more importantly, leave the page locked behind a panel CSS has hidden.
    window.addEventListener('resize', function () {
      if (window.innerWidth > breakpoint(root)) close();
    });
  }

  /*
   * Publish the header's height as --dw-header-h.
   *
   * The header is position:sticky, so anything else that wants to stick below
   * it needs to know how tall it is. --dw-scrim-top already exists but is not
   * usable for this: it is only refreshed while a mega panel is open (the
   * resize and scroll handlers both bail unless `current` is set), so the rest
   * of the time it holds a stale number or none at all.
   *
   * Purely additive — this only writes a custom property. Nothing here reads
   * it back, so a consumer that never loads costs nothing.
   */
  var headerHeightState = null;

  function publishHeaderHeight(root) {
    /*
     * A header edit in the theme editor fires shopify:section:load with a
     * brand new element, so this runs again. Tear the previous wiring down
     * first — otherwise every edit stacks another resize listener and another
     * ResizeObserver, all of them measuring elements that left the document.
     */
    if (headerHeightState) {
      window.removeEventListener('resize', headerHeightState.measure);
      if (headerHeightState.observer) headerHeightState.observer.disconnect();
      headerHeightState = null;
    }

    function measure() {
      /*
       * Only a sticky header occludes anything. The sticky rule is behind a
       * theme setting, so when a merchant turns it off the header scrolls away
       * with the page — and anything offsetting by its height would sit that
       * far down the viewport for no reason. Publish 0 in that case rather
       * than skipping the write, so the value is never merely stale.
       */
      var sticky = false;
      try {
        sticky = window.getComputedStyle(root).position === 'sticky';
      } catch (err) {
        sticky = false;
      }
      var h = sticky ? Math.round(root.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--dw-header-h', h + 'px');
    }

    var state = { measure: measure, observer: null };
    measure();
    window.addEventListener('resize', measure);
    if (window.ResizeObserver) {
      try {
        state.observer = new ResizeObserver(measure);
        state.observer.observe(root);
      } catch (err) { /* older engines */ }
    }
    // Webfonts land after first paint and can change the row height.
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(measure).catch(function () {});
    }
    headerHeightState = state;
  }

  /* ---- tabbed mega panels --------------------------------------------------
   *
   * A master collection's panel puts its categories down the left and that
   * category's products beside them. Hovering or focusing a tab switches which
   * products show; the tab is a real link, so clicking still opens the category
   * page.
   *
   * Bound on document rather than on the panel, because panel markup arrives by
   * innerHTML after a fetch — anything bound at setup time would never see it,
   * and a <script> inside the fetched section would not execute.
   *
   * mouseover rather than mouseenter: only the former bubbles, so only it can be
   * delegated.
   */
  function activateTab(tab) {
    var wrap = tab.closest('.dw-menu-panel__tabbed');
    if (!wrap) return;
    var key = tab.getAttribute('data-dw-tab');

    wrap.querySelectorAll('[data-dw-tab]').forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-active', on);
      // Only the active tab is tabbable, so the next Tab press falls into the
      // view that is showing instead of the next tab — which would hide it.
      t.setAttribute('tabindex', on ? '0' : '-1');
    });
    wrap.querySelectorAll('[data-dw-tab-view]').forEach(function (view) {
      view.hidden = view.getAttribute('data-dw-tab-view') !== key;
    });
  }

  function onTabPointer(e) {
    var tab = e.target && e.target.closest ? e.target.closest('[data-dw-tab]') : null;
    if (tab) activateTab(tab);
  }

  /* With a roving tabindex the arrow keys are the only way between tabs, and
     they are what a screen-reader user will reach for. focusin does the rest:
     moving focus to a tab activates it. */
  function onTabKey(e) {
    var tab = e.target && e.target.closest ? e.target.closest('[data-dw-tab]') : null;
    if (!tab) return;
    var list = tab.closest('[data-dw-tablist]');
    if (!list) return;

    var tabs = Array.prototype.slice.call(list.querySelectorAll('[data-dw-tab]'));
    var i = tabs.indexOf(tab);
    var next = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
    else if (e.key === 'Home') next = tabs[0];
    else if (e.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;

    e.preventDefault();
    next.focus();
  }

  document.addEventListener('mouseover', onTabPointer);
  document.addEventListener('focusin', onTabPointer);
  document.addEventListener('keydown', onTabKey);

  function init() {
    document.querySelectorAll('[data-dw-header]').forEach(setup);
    document.querySelectorAll('[data-dw-header]').forEach(publishHeaderHeight);
    document.querySelectorAll('[data-dw-header]').forEach(setupMega);
    document.querySelectorAll('[data-dw-search]').forEach(setupSearch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-dw-header]') : null;
    if (root) { setup(root); setupMega(root); publishHeaderHeight(root); }
    var form = e.target.querySelector ? e.target.querySelector('[data-dw-search]') : null;
    if (form) setupSearch(form);
  });
})();
